function readEnvFlag(value: unknown): boolean {
  if (value == null) return false;
  const v = String(value).trim().toLowerCase();
  return v === "1" || v === "true" || v === "yes" || v === "on";
}

export function isCameraMotionV2Enabled(): boolean {
  return readEnvFlag(import.meta.env.VITE_CAMERA_MOTION_V2);
}

export function isBodyFxRuntimeScanEnabled(): boolean {
  // Default on when flag is unset; explicit false only when set to 0/false/off.
  const raw = import.meta.env.VITE_STUDIO_BODY_FX_SCAN;
  if (raw == null) return true;
  return readEnvFlag(raw);
}

export const DETERMINISTIC_CLOCK_KEY = "vb:studio:deterministicClock";
export const WORKER_EFFECTS_KEY = "vb:studio:workerEffects";

/**
 * Module-level cache for the deterministic-clock flag.
 *
 * Why cached: `isDeterministicClockEnabled` is called on every playback tick
 * (~60Hz). At ~5-50µs per `localStorage.getItem` that adds up to 0.3-3ms/sec
 * of main-thread work for what is effectively a constant. We read once on
 * first call and re-read only on `storage` events (cross-tab) or explicit
 * `setDeterministicClockEnabled` calls (same-tab).
 *
 * Initial value `null` means "not yet read". The first call seeds the cache
 * AND installs the storage event listener.
 */
let _deterministicCached: boolean | null = null;

/** Module-level cache for the worker-effects flag (same pattern). */
let _workerEffectsCached: boolean | null = null;

let _storageListenerInstalled = false;

function _readDeterministicFlagFresh(): boolean {
  if (typeof window === "undefined") return true;
  try {
    // Default ON — disable with localStorage.setItem("vb:studio:deterministicClock", "0")
    return window.localStorage.getItem(DETERMINISTIC_CLOCK_KEY) !== "0";
  } catch {
    return true;
  }
}

function _readWorkerEffectsFlagFresh(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(WORKER_EFFECTS_KEY) === "1";
  } catch {
    return false;
  }
}

function _installStorageListener(): void {
  if (_storageListenerInstalled) return;
  if (typeof window === "undefined") return;
  try {
    window.addEventListener("storage", (e) => {
      if (e.key === DETERMINISTIC_CLOCK_KEY) {
        _deterministicCached = _readDeterministicFlagFresh();
      }
      if (e.key === WORKER_EFFECTS_KEY) {
        _workerEffectsCached = _readWorkerEffectsFlagFresh();
      }
    });
    _storageListenerInstalled = true;
  } catch {
    /* listener install failed — no-op, fall back to seeded value */
  }
}

/**
 * Deterministic timeline clock — when enabled, the playhead advances purely
 * via dt accumulation (the lead-video media-derived clock is bypassed).
 * Decoder stalls then produce visible video stutter instead of confusing
 * timeline stalls.
 *
 * ON by default (flipped 2026-04-09 after dogfooding). Disable from DevTools:
 *   localStorage.setItem("vb:studio:deterministicClock", "0");
 *   location.reload();
 *
 * The flag is memoized: only the first call reads localStorage. Subsequent
 * calls return the cached value. Cross-tab updates are picked up via the
 * `storage` event listener installed on first call. Same-tab updates via
 * `setDeterministicClockEnabled` flush the cache immediately.
 *
 * Tracked in docs/plans/2026-04-08-studio-canva-smoothness.md (Task B2 + B4).
 */
export function isDeterministicClockEnabled(): boolean {
  if (_deterministicCached === null) {
    _deterministicCached = _readDeterministicFlagFresh();
    _installStorageListener();
  }
  return _deterministicCached;
}

export function setDeterministicClockEnabled(enabled: boolean): void {
  if (typeof window === "undefined") {
    _deterministicCached = enabled;
    return;
  }
  try {
    if (enabled) {
      // Remove explicit key — default is now ON, so absence = enabled
      window.localStorage.removeItem(DETERMINISTIC_CLOCK_KEY);
    } else {
      // Explicit opt-out
      window.localStorage.setItem(DETERMINISTIC_CLOCK_KEY, "0");
    }
    _deterministicCached = enabled;
  } catch {
    /* localStorage unavailable (private browsing) — keep in-memory cache anyway */
    _deterministicCached = enabled;
  }
}

/**
 * Test-only helper: clear the memoized cache so the next call re-reads from
 * localStorage. Used by unit tests that mock `localStorage.getItem` and need
 * the function to actually call the mock.
 *
 * @internal
 */
export function _resetDeterministicClockCacheForTests(): void {
  _deterministicCached = null;
}

/**
 * Worker-effects flag — when enabled, FX frame processing is offloaded to an
 * OffscreenCanvas Web Worker instead of running on the main thread.
 *
 * Off by default. Enable from DevTools:
 *   localStorage.setItem("vb:studio:workerEffects", "1");
 *   location.reload();
 *
 * Same caching semantics as `isDeterministicClockEnabled`: memoized after
 * first call, invalidated by cross-tab `storage` events or same-tab
 * `setWorkerEffectsEnabled` calls.
 */
export function isWorkerEffectsEnabled(): boolean {
  if (_workerEffectsCached === null) {
    _workerEffectsCached = _readWorkerEffectsFlagFresh();
    _installStorageListener();
  }
  return _workerEffectsCached;
}

export function setWorkerEffectsEnabled(enabled: boolean): void {
  if (typeof window === "undefined") {
    _workerEffectsCached = enabled;
    return;
  }
  try {
    if (enabled) {
      window.localStorage.setItem(WORKER_EFFECTS_KEY, "1");
    } else {
      window.localStorage.removeItem(WORKER_EFFECTS_KEY);
    }
    _workerEffectsCached = enabled;
  } catch {
    /* localStorage unavailable (private browsing) — keep in-memory cache anyway */
    _workerEffectsCached = enabled;
  }
}

/**
 * Test-only helper: clear the memoized worker-effects cache.
 * @internal
 */
export function _resetWorkerEffectsCacheForTests(): void {
  _workerEffectsCached = null;
}
